<script setup>
import { computed, ref, watch } from 'vue'
import moment from 'moment-timezone'
import { storeToRefs } from 'pinia'
import { useReservationsStore, useUsersStore } from '@/stores'
import { numberWithCommas, simulation } from '@/plugins/misc'
import { projectedRequestExpirationLabel as projectedRequestExpirationLabelUtil } from '@core/utils/requestExpiration'
import { requiredValidator } from '@validators'
import Tooltip from '@/layouts/components/Tooltip.vue'
import MemberProfileCard from '@core/components/MemberProfileCard.vue'
import axios from '@axios'
import { inRestrictedSimulation } from '@/utils/simulationScopes'

const props = defineProps({
  selectedRestaurants: { type: Array, default: () => [] },
  selectedRestaurantsAlphabetical: { type: Array, default: () => [] },
  selectedRestaurantsNotPreferred: { type: Array, default: () => [] },
  selectedRestaurantsNotAcceptingReferrals: { type: Array, default: null },
  selectedRestaurantNames: { type: String, default: '' },
  restaurantsForSelectedCity: { type: Array, default: () => [] },
  allTimeOptions: { type: Array, default: () => [] },
  pricingBreakdown: { type: Object, default: null },
  dateContextSubtitle: { type: String, default: '' },
  dateSelectedPastPlatinumMembershipTerm: { type: Boolean, default: false },
  dateSelectedPastPreferredMembershipTerm: { type: Boolean, default: false },
  isTrialAndAlreadyHasAReservation: { type: Boolean, default: false },
  isNotHost: { type: Boolean, default: false },
  isProfileIncomplete: { type: Boolean, default: false },
  profileValidationMessage: { type: String, default: '' },
  // When true (Review-is-final), hide the step's own 'Next' CTA — the Submit
  // lives in the payment step rendered alongside it. Uses v-show so the
  // v-if/else-if chain still resolves here and the upgrade fallback (the v-else
  // after Next) does not appear in its place.
  hideNext: { type: Boolean, default: false },
  // Which part of the review to render: 'review' (summary only) or 'profile'
  // (profile card + the proceed/edit-profile buttons). Splits the old combined
  // step across the Review and Your-Profile screens.
  section: { type: String, default: 'review' },
})

const emit = defineEmits(['next', 'upgrade'])

// RM-337 item 5: "Update Profile" for another member opens an act-as session,
// which a limited act-as session can't start.
const restrictedSession = inRestrictedSimulation()

const usersStore = useUsersStore()
const reservationsStore = useReservationsStore()
const { myUser, loginDialogMode } = storeToRefs(usersStore)
const { editingReservation, confirmedPresence } = storeToRefs(reservationsStore)

// "What the restaurant will see about you" — the exact restaurant-facing block
// (favorites/connections, in-group Fan list, booking history), shown on the
// Your-Profile review step so the member sees it before submitting. Scoped to
// the primary selected restaurant (its group drives the Fan + group lines).
const memberPreviewBlock = ref(null)
const previewRestaurant = computed(() => props.selectedRestaurants?.[0] || null)
const previewOwnerEmail = computed(() => (editingReservation.value?.owner?.email || myUser.value?.email || ''))

async function fetchMemberPreviewBlock() {
  const restaurantId = previewRestaurant.value?._id
  const email = previewOwnerEmail.value
  if (props.section !== 'profile' || !restaurantId || !email) {
    memberPreviewBlock.value = null

    return
  }
  try {
    const { data } = await axios.get('/reservation/member-preview-block', { params: { restaurantId, email } })
    memberPreviewBlock.value = data?.block || null
  } catch (e) {
    memberPreviewBlock.value = null
  }
}

watch(
  [() => props.section, () => previewRestaurant.value?._id, previewOwnerEmail],
  fetchMemberPreviewBlock,
  { immediate: true },
)

// In-group favorites line ("Favorites in {group}: …"), shown below the bookings.
// Split into the label (through the colon) and the venue list so the label can be
// bolded — presentation only; the underlying string is the shared block's `fanLine`.
const favoritesLine = computed(() => {
  const line = memberPreviewBlock.value?.fanLine
  if (!line) return null
  const idx = line.indexOf(': ')
  if (idx === -1) return { label: '', names: line }

  return { label: line.slice(0, idx + 1), names: line.slice(idx + 2) }
})

// Host Profile (7b) presence sign-off: the Next button stays fully enabled; if a
// member taps it without checking the box they get an inline error instead of a
// greyed-out button. The error clears as soon as they check it.
const presenceError = ref(false)
watch(confirmedPresence, checked => {
  if (checked) presenceError.value = false
})

const handleProfileNext = () => {
  if (!confirmedPresence.value) {
    presenceError.value = true

    return
  }
  presenceError.value = false
  reservationsStore.saveEditReservation(true)
  emit('next')
}

// RM-150: gate the submit on the OWNER's membership, not the actor's.
// `editingReservation.owner` is auto-populated server-side with `isPlatinum`,
// `isPreferred`, `activeSubscription`, `platinumRenewalDate`, `premiumRenewalDate`
// (see models/Reservation.js toJSON whitelist). For self-bookings the lean POST
// response has no owner, so we fall back to myUser.
// RM-154 follow-up: see EditReservationDialog.vue for the partial-owner-object
// flaw and the truthy-wins rationale. Self-booking actor + stale owner stub
// (no isPlatinum, or explicit false) was leaking non-Platinum UI to Platinum
// members. Normalize email comparison, allow ObjectId/string _id matching,
// and truthy-union the membership flags for self-bookings.
const reservationOwner = computed(() => {
  const owner = editingReservation.value?.owner
  const me = myUser.value
  if (!owner) return me || {}
  if (!owner.email && !owner._id) return me || {}

  const ownerEmail = String(owner.email || '').toLowerCase().trim()
  const meEmail = String(me?.email || '').toLowerCase().trim()
  const isSelfBooking = me && (
    (ownerEmail && ownerEmail === meEmail)
    || (owner._id && me._id && String(owner._id) === String(me._id))
  )

  if (isSelfBooking) {
    return {
      ...me,
      ...owner,
      isPlatinum: !!(owner.isPlatinum || me.isPlatinum),
      isPreferred: !!(owner.isPreferred || me.isPreferred),
      activeSubscription: owner.activeSubscription || me.activeSubscription,
    }
  }

  return owner
})

// RM-162 — projected request expiration, computed locally from the store +
// selected restaurants so the summary never depends on a prop round-trip from
// the parent. Shown for every request (see client/src/@core/utils/requestExpiration.js).
const projectedExpirationLabel = computed(() => {
  const tz = props.selectedRestaurants?.[0]?.timeZoneIdentifier
    || editingReservation.value?.timeZoneIdentifier

  return projectedRequestExpirationLabelUtil(
    editingReservation.value,
    props.selectedRestaurants,
    tz,
  )
})

// RM-150: when an admin account is booking on behalf of a member, edit the
// MEMBER's profile rather than the admin's own. Switch context via simulation
// so the existing edit dialog operates on the member's data. Land the admin
// on the member's profile page with ?openEditProfile=1 so the dialog opens
// automatically — without that they'd hit the home feed and have to navigate.
// The draft is saved first so the admin can resume after exiting simulation.
async function editProfileForContext() {
  await reservationsStore.saveEditReservation(true)
  const owner = editingReservation.value?.owner
  if (props.isNotHost && owner?.email) {
    const goTo = owner.profileId
      ? `/m/${owner.profileId}?openEditProfile=1`
      : '/?openEditProfile=1'
    simulation(owner.email, { goTo })

    return
  }
  usersStore.editMyUser()
}
</script>

<template>
  <div>
    <div class="review-step-header">
      <span class="review-step-title">{{ section === 'profile' ? 'Member Profile' : 'Request Summary' }}</span>
      <span
        v-if="dateContextSubtitle && section !== 'profile'"
        class="review-step-subtitle"
      >{{ dateContextSubtitle }}</span>
    </div>
    <div class="mx-4 mb-8 tab-content">
      <div
        v-if="editingReservation && section === 'review'"
        class="review-summary-card mb-4"
      >
        <div class="review-restaurant-name">
          {{ selectedRestaurants[0]?.name }}
          <span class="review-restaurant-market">{{ selectedRestaurants[0]?.market }}</span>
        </div>

        <div class="review-details-grid">
          <div class="review-detail-item">
            <span class="review-detail-label">Date</span>
            <span class="review-detail-value">{{ editingReservation.date ? moment(editingReservation.date).format('dddd, M/D') : '' }}</span>
          </div>
          <div class="review-detail-item">
            <span class="review-detail-label">Party Size</span>
            <span class="review-detail-value">{{ editingReservation.partySize }}</span>
          </div>
          <div class="review-detail-item">
            <span class="review-detail-label">Preferred Time</span>
            <span class="review-detail-value">{{ editingReservation.preferredTime }}</span>
          </div>
          <div
            v-if="editingReservation.earliestTime && editingReservation.latestTime"
            class="review-detail-item"
          >
            <span class="review-detail-label">Starting Time Range</span>
            <span class="review-detail-value">{{ editingReservation.earliestTime }}–{{ editingReservation.latestTime }}</span>
          </div>
          <div class="review-detail-item">
            <span class="review-detail-label">Occasion</span>
            <span class="review-detail-value">{{ editingReservation.occasion }}<span v-if="editingReservation.occasionDetails"> – {{ editingReservation.occasionDetails }}</span></span>
          </div>
          <div
            v-if="pricingBreakdown"
            class="review-detail-item"
          >
            <span class="review-detail-label">F&B Spend</span>
            <span class="review-detail-value">${{ numberWithCommas(pricingBreakdown.baseSpend) }} (${{ numberWithCommas(pricingBreakdown.minSpendPerPerson) }}pp)</span>
          </div>
          <div
            v-if="projectedExpirationLabel"
            class="review-detail-item"
          >
            <span class="review-detail-label">Request Expires</span>
            <span class="review-detail-value">{{ projectedExpirationLabel }}</span>
          </div>
        </div>

        <div
          v-if="editingReservation.additionalNotes"
          class="review-notes"
        >
          <span class="review-detail-label">Notes</span>
          <!--
            Rendered as PLAIN TEXT (not v-html): additionalNotes is member
            free-text and never needs HTML. `white-space: pre-wrap` preserves the
            newlines the old `\n -> <br/>` replace produced, with zero XSS surface.
          -->
          <span class="review-notes-text">{{ editingReservation.additionalNotes }}</span>
        </div>
      </div>

      <div
        v-if="editingReservation && section === 'profile'"
        class="review-profile-card-wrapper"
      >
        <div class="review-profile-nudge">
          <div class="review-profile-nudge-text">
            Included in request
          </div>
          <div class="review-profile-nudge-text">
            Response &amp; acceptance vary by profile &amp; spend
          </div>
        </div>

        <!-- Update Profile moved above the card (matches the host-profile layout). -->
        <div
          v-if="myUser?.userLevel != 'dummy' && !(isNotHost && restrictedSession)"
          class="review-profile-edit-top"
        >
          <VBtn
            variant="text"
            density="compact"
            size="small"
            color="primary-dark"
            prepend-icon="mdi-pencil-outline"
            @click="editProfileForContext"
          >
            Update Profile
          </VBtn>
        </div>

        <MemberProfileCard
          :member="editingReservation.owner || myUser"
          :editable="false"
          flat
        >
          <!-- Favorites • Connections counts, directly under the name. -->
          <template
            v-if="memberPreviewBlock"
            #affinity
          >
            <div class="member-affinity__counts">
              {{ memberPreviewBlock.memberSummaryLine }}
            </div>
          </template>
        </MemberProfileCard>

        <!-- Prior bookings via RM, below the profile — mirrors the request email + response page. -->
        <div
          v-if="memberPreviewBlock && memberPreviewBlock.bookingHeading"
          class="member-bookings"
        >
          <div class="member-bookings__heading">
            {{ memberPreviewBlock.bookingHeading }}
          </div>
          <div
            v-for="(line, lineIdx) in memberPreviewBlock.bookingLines"
            :key="`mb-line-${lineIdx}`"
            class="member-bookings__line"
          >
            {{ line }}
          </div>
        </div>

        <!-- In-group favorites — only for group restaurants with prior group bookings. -->
        <div
          v-if="favoritesLine"
          class="member-favorites"
        >
          <span class="member-favorites__label">{{ favoritesLine.label }}</span> {{ favoritesLine.names }}
        </div>

        <!-- Member commitment — collapsible, sits directly above the "I confirm" checkbox. -->
        <div
          v-if="editingReservation.status == 'Draft' && reservationOwner.activeSubscription && !(selectedRestaurantsNotPreferred && !reservationOwner.isPlatinum)"
          class="commitment-wrap mt-4"
        >
          <details class="commitment-section">
            <summary
              class="commitment-header"
              style="cursor: pointer; list-style: none;"
            >
              <VIcon
                icon="mdi-handshake-outline"
                size="18"
                class="commitment-icon"
              />
              <span class="commitment-title">{{ $t('member.commitment.title') }}</span>
              <span style="margin-left: auto; font-size: 0.7rem; color: #666;">▸</span>
            </summary>
            <div class="commitment-body">
              <p class="commitment-intro">
                {{ $t('member.commitment.intro') }}
              </p>
              <div
                class="commitment-bullets"
                v-html="$t('member.commitment.bullets')"
              />
              <p class="commitment-text">
                {{ $t('member.commitment.consequences') }}
              </p>
              <p class="commitment-closing">
                {{ $t('member.commitment.closing') }}
              </p>
            </div>
          </details>
        </div>

        <!-- Presence / member-commitment sign-off -->
        <div
          v-if="editingReservation.status == 'Draft' && reservationOwner.activeSubscription && !(selectedRestaurantsNotPreferred && !reservationOwner.isPlatinum)"
          class="text-center mt-2"
        >
          <div
            class="confirm-hold-card"
            :class="{ 'confirm-hold-card--active': confirmedPresence }"
            @click="confirmedPresence = !confirmedPresence"
          >
            <div class="confirm-hold-checkbox-wrap">
              <VCheckbox
                v-model="confirmedPresence"
                density="compact"
                color="primary-dark"
                hide-details
                class="confirm-hold-checkbox"
                @click.stop
              />
            </div>
            <span class="confirm-hold-label">
              <template v-if="isNotHost">
                <span class="confirm-line">I confirm {{ editingReservation.owner?.firstName }} will be present for the reservation if accepted</span>
                <span class="confirm-line">Reservations via RM are non-transferable</span>
                <span class="confirm-line">Booking for anyone else will result in cancellation &amp; full charge of the guaranteed spend</span>
              </template>
              <template v-else>
                <span class="confirm-line">I confirm I'll be present for the reservation if accepted</span>
                <span class="confirm-line">Reservations via RM are non-transferable</span>
                <span class="confirm-line">Booking for anyone else will result in cancellation &amp; full charge of your guaranteed spend</span>
              </template>
            </span>
          </div>
          <div
            v-if="presenceError"
            class="mt-2 text-error text-sm text-center"
          >
            Confirmation required to proceed
          </div>
        </div>
      </div>

      <template v-if="myUser.userLevel == 'admin' && editingReservation.status == 'Accepted' && section === 'profile'">
        <VCol cols="12">
          <VCombobox
            v-model="editingReservation.confirmedRestaurant"
            :label="$t('reservation.labels.confirmedRestaurant')"
            :items="restaurantsForSelectedCity"
            :menu-props="{height:($vuetify.display.mdAndDown ? 250 : 650) }"
            :hide-no-data="false"
            item-title="text"
            item-value="value"
            :return-object="false"
            chips
            closable-chips
            required
            hide-selected
          >
            <template #prepend-inner>
              <VIcon>mdi-magnify</VIcon>
            </template>
            <template #no-data>
              <VListItem>
                <VListItemTitle>
                  {{ $t('reservation.labels.noMatchesWriteIn') }}
                </VListItemTitle>
              </VListItem>
            </template>
          </VCombobox>
          <Tooltip text="The time the restaurant used to make the reservation." />
          <VSelect
            v-model="editingReservation.confirmedTime"
            :label="$t('reservation.labels.confirmedTime')"
            :items="allTimeOptions"
            :no-data-text="$t('reservation.labels.noTimesAvailable')"
            :disabled="!editingReservation.date"
          />
          <Tooltip text="The final party size" />
          <VTextField
            v-model="editingReservation.partySize"
            label="Party Size"
            :rules="[requiredValidator]"
            required
            type="number"
          />
          <VTextField
            v-model="editingReservation.minSpendPerPerson"
            class="min-spend-per-person-select mt-4"
            label="Budget pp (Pre-Tax & Tip)"
            :rules="[requiredValidator]"
            required
            type="number"
            prefix="$"
          />
        </VCol>
      </template>

      <div
        v-if="section === 'profile'"
        class="mt-2"
      >
        <div
          v-if="(reservationOwner.isPlatinum && dateSelectedPastPlatinumMembershipTerm) || (reservationOwner.isPreferred && !editingReservation.requiresPlatinum && dateSelectedPastPreferredMembershipTerm)"
          class="text-caption text-center"
        >
          Request details will be saved in the interim
        </div>
        <div
          v-else-if="isTrialAndAlreadyHasAReservation"
          class="text-caption text-center"
        >
          Request details will be saved in the interim
        </div>
        <div
          v-else-if="!isNotHost && myUser.activeSubscription && isProfileIncomplete"
          class="text-caption text-center"
        >
          Request details will be saved in the interim
        </div>
        <div
          v-else-if="selectedRestaurantsNotAcceptingReferrals"
          class="text-caption text-center"
        />
        <div
          v-else-if="reservationOwner.activeSubscription && !(selectedRestaurantsNotPreferred && !reservationOwner.isPlatinum)"
          class="text-caption text-center"
        />
        <div
          v-else
          class="text-caption text-center"
        >
          Request details will be saved in the interim
        </div>
      </div>

      <VCol
        v-if="section === 'profile' && myUser?.primaryGroupPermission == 'pending'"
        cols="12"
      >
        <VAlert
          class="ml-2 mr-2 mb-2"
          type="warning"
        >
          <div>{{ $t('reservation.validation.membershipPending') }}</div>
          <div class="mt-2">
            questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
          </div>
        </VAlert>
      </VCol>
      <VCol
        v-else-if="section === 'profile' && editingReservation.status == 'Draft'"
        cols="12"
      >
        <VAlert
          v-if="reservationOwner.isPlatinum && dateSelectedPastPlatinumMembershipTerm"
          class="ml-2 mr-2 mb-2"
          type="warning"
        >
          <div>Your Platinum membership expires at midnight CT on {{ moment(reservationOwner?.platinumRenewalDate).format("M/D") }}, you selected a date past that. Extend your membership or change the date to submit.</div>
          <div class="mt-2">
            questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
          </div>
        </VAlert>
        <VAlert
          v-else-if="reservationOwner.isPreferred && !editingReservation.requiresPlatinum && dateSelectedPastPreferredMembershipTerm"
          class="ml-2 mr-2 mb-2"
          type="warning"
        >
          <div>Your membership expires at midnight CT on {{ moment(reservationOwner?.premiumRenewalDate).format("M/D") }}, you selected a date past that. Extend your membership or change the date to submit.</div>
          <div class="mt-2">
            questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
          </div>
        </VAlert>

        <VAlert
          v-else-if="!isNotHost && myUser.activeSubscription && isProfileIncomplete"
          class="ml-2 mr-2 mb-2"
          type="warning"
        >
          <div>{{ profileValidationMessage }}, your request will be saved in the interim</div>
          <div class="mt-2">
            questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
          </div>
        </VAlert>
        <VAlert
          v-else-if="selectedRestaurantsNotPreferred && !reservationOwner.isPlatinum"
          class="ml-2 mr-2 mb-2"
          type="warning"
        >
          <div>{{ $t('reservation.validation.platinumRequired') }}</div>
          <div class="mt-2">
            questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
          </div>
        </VAlert>
        <VAlert
          v-else-if="!reservationOwner.activeSubscription"
          class="ml-2 mr-2"
          type="warning"
        >
          <div>{{ $t('reservation.validation.platinumRequiredPreferred') }}</div>
          <div class="mt-2">
            questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
          </div>
        </VAlert>
      </VCol>

      <VCol
        v-if="section === 'profile'"
        cols="12"
        class="d-flex flex-wrap justify-center gap-4 py-2"
      >
        <VSpacer />
        <template v-if="myUser?.primaryGroupPermission == 'pending'">
          <VBtn
            v-if="!isNotHost && isProfileIncomplete"
            @click="reservationsStore.saveEditReservation(true); usersStore.editMyUser()"
          >
            {{ $t('reservation.actions.completeProfile') }}
          </VBtn>
          <VBtn
            v-else
            @click="reservationsStore.saveEditReservation();"
          >
            {{ $t('reservation.actions.saveAndClose') }}
          </VBtn>
        </template>
        <template v-else-if="editingReservation.status == 'Draft'">
          <div
            v-if="myUser?.userLevel == 'dummy'"
            class="text-center"
          >
            <div class="mt-0">
              <VBtn
                variant="flat"
                class="px-6"
                size="small"
                color="primary"
                @click="loginDialogMode = 'activate';"
              >
                <span>{{ $t('menu.requestToJoin') }}</span>
              </VBtn>
              <VBtn
                variant="flat"
                class="ml-2"
                size="small"
                color="#efefef"
                style="color: #222222;"
                @click="loginDialogMode = 'login';"
              >
                <span>{{ $t('menu.logIn') }}</span>
              </VBtn>
            </div>
          </div>
          <VBtn
            v-else-if="(reservationOwner.isPlatinum && dateSelectedPastPlatinumMembershipTerm) || (reservationOwner.isPreferred && !editingReservation.requiresPlatinum && dateSelectedPastPreferredMembershipTerm)"
            @click="emit('upgrade')"
          >
            {{ $t('reservation.actions.extendMembership') }}
          </VBtn>
          <VBtn
            v-else-if="!isNotHost && myUser.activeSubscription && isProfileIncomplete"
            @click="reservationsStore.saveEditReservation(true); usersStore.editMyUser()"
          >
            {{ $t('reservation.actions.completeProfile') }}
          </VBtn>
          <div v-else-if="selectedRestaurantsNotAcceptingReferrals">
            {{ selectedRestaurantsNotAcceptingReferrals.join(", ") }} 🚫 {{ $t('reservation.preferred.notAcceptingReferrals') }}
          </div>
          <VBtn
            v-else-if="reservationOwner.activeSubscription && !(selectedRestaurantsNotPreferred && !reservationOwner.isPlatinum)"
            v-show="!hideNext"
            color="primary-dark"
            size="large"
            block
            @click="handleProfileNext"
          >
            Next
          </VBtn>
          <VBtn
            v-else
            @click="emit('upgrade')"
          >
            {{ $t('reservation.actions.upgradeMembership') }}
          </VBtn>
        </template>
        <template v-else-if="editingReservation.submittedAt">
          <VBtn @click="reservationsStore.saveEditReservation();">
            {{ $t('reservation.actions.save') }}
          </VBtn>
        </template>
        <VSpacer />
      </VCol>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tab-content {
  position: relative;
}

.review-step-header {
  text-align: center;
  padding: 12px 0 4px;
}

.review-step-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
}

.review-step-subtitle {
  font-size: 14px;
  font-weight: 500;
  color: #666;
  display: block;
  margin-top: 4px;
}

.review-summary-card {
  background: #f8f8f9;
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
}

.review-restaurant-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 12px;
  line-height: 1.3;
}

.review-restaurant-market {
  font-size: 14px;
  font-weight: 400;
  color: #999;
  display: block;
  margin-top: 2px;
}

.review-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.review-detail-item {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.review-detail-label {
  font-size: 12px;
  color: #999;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.review-detail-value {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
}

.review-notes {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.review-notes-text {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  white-space: pre-wrap;
}

.review-spend-footer {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
  font-size: 12px;
  color: #999;
  line-height: 1.5;
}

.review-profile-card-wrapper {
  margin-top: 6px;
}

.review-profile-nudge {
  margin-bottom: 12px;
}

.review-profile-nudge-text {
  font-size: 14px;
  font-style: italic;
  color: var(--ds-text-secondary, #595959);
  text-align: center;
  line-height: 1.4;
}

/* Update Profile — centred above the profile card. */
.review-profile-edit-top {
  text-align: center;
  margin-bottom: 8px;

  :deep(.v-btn) {
    font-size: 15px;
  }
}

/* Favorites • Connections counts, directly under the name inside the centred card. */
.member-affinity__counts {
  margin: 8px 0 2px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--ds-text-strong, #1a1a1a);
}

/* Prior bookings via RM — below the profile card, mirrors the restaurant's view
   in the request email and on the referral response page. */
.member-bookings {
  margin-top: 16px;
  text-align: center;
}

.member-bookings__heading {
  font-size: 14px;
  font-weight: 700;
  color: var(--ds-text-strong, #1a1a1a);
}

.member-bookings__line {
  margin-top: 4px;
  font-size: 14px;
  line-height: 1.4;
  color: var(--ds-text-primary, #3A3541);
}

/* In-group favorites — modest space below the bookings (no divider). The label is
   intentionally NOT bold so it doesn't outweigh the bookings heading. */
.member-favorites {
  margin-top: 10px;
  text-align: center;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ds-text-primary, #3A3541);
}

.min-spend-per-person-select :deep(.v-field__field) {
  white-space: unset;
  box-sizing: content-box;
  padding-top: 6px;
}

:deep(.smaller-label .v-label) {
  font-size: 16px !important;
}

.confirm-hold-card {
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 14px;
  background: var(--ds-surface-card, #fff);
  border: 1px solid var(--ds-border-light, #e0e0e0);
  border-radius: 12px;
  padding: 16px 18px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  text-align: left;
}

.confirm-hold-card--active {
  border-color: var(--ds-brand-primary-dark);
  box-shadow: 0 0 0 1px var(--ds-brand-primary-dark);
}

/* VCheckbox's own root collapses to ~0 width while its 28px square overflows,
   which made the square overlap the label text (the "I" of "I confirm" hid behind
   it). Wrap it in a real 28px box so flex lays out the square + gap + label
   correctly, and pin the inner control so it fills that box. */
.confirm-hold-checkbox-wrap {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  overflow: visible;
}

.confirm-hold-checkbox {
  width: 28px;
  height: 28px;
}

.confirm-hold-checkbox :deep(.v-selection-control),
.confirm-hold-checkbox :deep(.v-selection-control__wrapper),
.confirm-hold-checkbox :deep(.v-selection-control__input) {
  width: 28px;
  height: 28px;
  min-height: auto;
}

.confirm-hold-checkbox :deep(.v-selection-control__input i) {
  border-radius: 6px;
}

.confirm-hold-label {
  font-size: 16px;
  font-weight: 400;
  color: var(--ds-text-dark, #1a1a1a);
  line-height: 1.4;
}

/* Each confirmation term on its own line. */
.confirm-line {
  display: block;
}

.confirm-line + .confirm-line {
  margin-top: 8px;
}

/* Member commitment — collapsible boxed card directly above the "I confirm"
   checkbox, with text the size of the bookings heading (14px). */
.commitment-wrap {
  text-align: left;
}

.commitment-section {
  background: var(--ds-surface-card, #fff);
  border: 1px solid var(--ds-border-light, #e0e0e0);
  border-radius: 12px;
  padding: 16px;
}

.commitment-header {
  display: flex;
  align-items: center;
  gap: 8px;

  &::-webkit-details-marker {
    display: none;
  }
}

.commitment-icon {
  color: var(--ds-brand-primary-dark);
}

.commitment-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--ds-text-dark, #1a1a1a);
}

details.commitment-section[open] .commitment-header span:last-child {
  transform: rotate(90deg);
  display: inline-block;
}

.commitment-body {
  margin-top: 10px;
}

.commitment-intro {
  font-size: 14px;
  color: var(--ds-text-medium, #555);
  line-height: 1.5;
  white-space: pre-line;
  margin: 0 0 10px 0;
}

.commitment-bullets {
  font-size: 14px;
  color: var(--ds-text-medium, #555);
  margin-bottom: 10px;

  :deep(ul) {
    padding-inline-start: 1.25rem;
    margin: 0;
  }

  :deep(li) {
    margin-bottom: 6px;
    line-height: 1.45;
  }
}

.commitment-text {
  font-size: 14px;
  color: var(--ds-text-medium, #555);
  line-height: 1.5;
  margin: 0 0 10px 0;
}

.commitment-closing {
  font-size: 14px;
  font-style: italic;
  color: var(--ds-text-light, #777);
  line-height: 1.5;
  margin: 0;
}
</style>
